import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';

import gsap, { Power1 } from 'gsap';

import { getIcon } from './input-helpers';
import { Wrapper } from './Input.styles';
import { danger, neutral, success } from 'theme/colors';
import { useCallback } from 'react';

// valid states
const VALID_STATES = {
  valid: 'valid',
  neutral: 'neutral',
  invalid: 'invalid',
};

// active states define the focus/blur animation
// as open being the input focused or filled with the placeholder on top
// as close being the input blured and unfilled with the placeholder in the middle
const ACTIVE_STATES = {
  open: 'open',
  close: 'close',
};

const Input = forwardRef((props, $ref) => {
  // PROPS
  const {
    label,
    slug,
    type,
    required,
    half,
    defaultValue,
    validation,
    errorMessage,
    onChange,
    onFocus,
    onBlur,
    options,
    ...extProps
  } = props;
  // PROPS

  // INITIALVALUES
  // helper funtions to determine the inital values for active and valid
  const InitialValues = {
    getActiveState: defaultValue ? ACTIVE_STATES.open : ACTIVE_STATES.close,
    getValidState:
      validation && defaultValue
        ? validation(defaultValue)
          ? VALID_STATES.valid
          : VALID_STATES.invalid
        : VALID_STATES.neutral,
  };
  // INITIALVALUES

  // REF
  const wrapperRef = useRef();
  const inputRef = useRef();
  const labelRef = useRef();
  // REF

  //ANIMATION
  const animations = {
    focusAnimation: () => {
      if (valid === VALID_STATES.neutral) {
        gsap
          .timeline()
          .to(inputRef.current, {
            borderColor: neutral.neu09,
            duration: 0.15,
          })
          .to(labelRef.current, {
            color: neutral.neu09,
            duration: 0.15,
            delay: -0.15,
          });
      }
      gsap.timeline().to(labelRef.current, {
        top: 0,
        scale: 0.85,
        duration: 0.15,
        ease: Power1.easeOut,
      });
    },
    blurAnimation: () => {
      if (valid === VALID_STATES.neutral) {
        gsap.timeline().to(inputRef.current, {
          borderColor: neutral.neu06,
          duration: 0.15,
        });
      }
      gsap.timeline().to(labelRef.current, {
        top: 27,
        scale: 1,
        duration: 0.15,
        ease: Power1.easeOut,
      });
    },
    neutralAnimation: () => {
      gsap
        .timeline()
        .to(inputRef.current, {
          borderColor: neutral.neu06,
          duration: 0.15,
        })
        .to(labelRef.current, {
          color: neutral.neu06,
          duration: 0.15,
          delay: -0.15,
          ease: Power1.easeOut,
        });
    },
    invalidateAnimation: () => {
      gsap
        .timeline()
        .to(inputRef.current, {
          borderColor: danger.default,
          duration: 0.15,
        })
        .to(labelRef.current, {
          color: danger.default,
          duration: 0.15,
          delay: -0.15,
          ease: Power1.easeOut,
        });
    },
    validateAnimation: () => {
      gsap
        .timeline()
        .to(inputRef.current, {
          borderColor: success.default,
          duration: 0.15,
        })
        .to(labelRef.current, {
          color: success.default,
          duration: 0.15,
          delay: -0.15,
          ease: Power1.easeOut,
        });
    },
  };
  //ANIMATION

  // STATES
  // set icon on input, only used in certain cases
  // date and password
  const [icon, setIcon] = useState(getIcon(type));
  // toggle open/close state
  // mainly registered in label position and color (shades of grey)
  // neu06 if blured; neu12 if active
  const [active, setActive] = useState(InitialValues.getActiveState);
  // toggle validation state
  // 3 states: valid, invalid and neutral
  // mainly registered in label color, border color and icon
  const [valid, setValid] = useState(InitialValues.getValidState);
  // input value, can have default value
  const [val, setVal] = useState(defaultValue ?? '');
  // STATES

  // VALIDATE FUNCTION
  const validate = (text) => {
    if (!text.length) {
      if (!required) {
        return VALID_STATES.neutral;
      } else {
        return VALID_STATES.invalid;
      }
    }
    if (validation) {
      if (validation(text)) {
        return VALID_STATES.valid;
      } else if (!validation(text)) {
        return VALID_STATES.invalid;
      }
    }

    return VALID_STATES.neutral;
  };
  // VALIDATE FUNCTION

  // INPUT BEHAVIOR FUNCTIONS
  const onChangeInput = (text) => {
    setVal(text);
    setActive(ACTIVE_STATES.open);

    const isValid = validate(text);

    if (isValid !== VALID_STATES.invalid) {
      setValid(validate(text));
    } else {
      setValid(VALID_STATES.neutral);
    }

    if (onChange) {
      onChange(text);
    }
  };

  const onBlurInput = useCallback(() => {
    if (!val.length) {
      setActive(ACTIVE_STATES.close);
    }

    const isValid = validate(val);

    if (type !== 'file' && isValid === VALID_STATES.invalid) {
      setValid(validate(val));
    }

    if (onBlur) {
      onBlur(val);
    }
  }, [val]);

  const onFocusInput = () => {
    setActive(ACTIVE_STATES.open);

    if (onFocus) {
      onFocus(val);
    }
  };
  // INPUT BEHAVIOR FUNCTIONS

  // IMPERATIVEHANDLE
  useImperativeHandle(
    $ref,
    () => ({
      ref: inputRef,
      value: val,
      valid: valid === VALID_STATES.valid ? true : false,
      isFocused: inputRef.current,
      setValue: (e) => {
        setVal(e);
        setActive(ACTIVE_STATES.open);
        setValid(validate(e));
      },
      clearValue: () => {
        setVal('');
        inputRef.current.value = '';
        setActive(ACTIVE_STATES.close);
        setValid(VALID_STATES.neutral);
      },
      setInvalid: () => setValid(VALID_STATES.invalid),
      focusInput: () => inputRef.current,
      blurInput: () => inputRef.current,
      forceValidation: () => setValid(validate(val)),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [val, valid]
  );
  // IMPERATIVEHANDLE

  // // EFFECTS
  useEffect(() => {
    active === ACTIVE_STATES.open
      ? animations.focusAnimation()
      : animations.blurAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    switch (valid) {
      case VALID_STATES.invalid:
        animations.invalidateAnimation();
        break;
      case VALID_STATES.valid:
        animations.validateAnimation();
        break;
      case VALID_STATES.neutral:
        animations.neutralAnimation();
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid]);

  useEffect(() => {
    // Change option selected
    const label = document.querySelector(
      `#${slug}-select .input__select-filter-selected`
    );
    const options = Array.from(
      document.querySelectorAll(`#${slug}-select .input__select-select-option`)
    );

    options.forEach((option) => {
      option.addEventListener('click', () => {
        label.textContent = option.textContent;
      });
    });
  }, []);

  // EFFECTS

  return (
    <Wrapper half={half} ref={wrapperRef} icon={!!icon}>
      <div
        id={`${slug}-select`}
        className={
          type === 'hour'
            ? 'input__input input__select'
            : type === 'file'
            ? 'input__input input__input--file'
            : 'input__input'
        }
      >
        <label htmlFor={slug} ref={labelRef}>
          {label}
          {required && ' *'}
        </label>
        {type === 'textarea' ? (
          <textarea
            ref={inputRef}
            id={slug}
            onInput={(e) => onChangeInput(e.currentTarget.value)}
            onFocus={(e) => onFocusInput()}
            onBlur={(e) => onBlurInput()}
            name={type}
            rows={8}
          ></textarea>
        ) : type === 'hour' ? (
          <>
            <input
              type="text"
              className="input__select-switch"
              id={slug}
              hidden
            />

            <ul
              ref={inputRef}
              className="input__select-filter"
              role="listbox"
              tabIndex="-1"
              onFocus={(e) => {
                onFocusInput();
                document
                  .querySelector(`#${slug}-select.input__select`)
                  ?.classList?.add('input__select--active');
              }}
              onBlur={(e) => {
                onBlurInput();
                document
                  .querySelector(`#${slug}-select.input__select`)
                  ?.classList?.remove('input__select--active');
              }}
            >
              <li
                className="input__select-filter-selected"
                aria-selected="true"
                onClick={() => {
                  type === 'hour' &&
                    document
                      .querySelector(`#${slug}-select.input__select`)
                      ?.classList?.add('input__select--active');
                }}
              ></li>
              <li>
                <ul className="input__select-select">
                  {options?.map((option) => {
                    return (
                      <li
                        key={option}
                        className="input__select-select-option"
                        role="option"
                        onClick={() => {
                          setVal(option);
                          onChangeInput(option);
                          document
                            .querySelector(`#${slug}-select.input__select`)
                            ?.classList?.remove('input__select--active');
                        }}
                      >
                        {option}
                      </li>
                    );
                  })}
                </ul>
              </li>
            </ul>
          </>
        ) : (
          <input
            ref={inputRef}
            id={slug}
            autoComplete="true"
            onInput={(e) => onChangeInput(e.currentTarget.value)}
            onFocus={(e) => onFocusInput()}
            onBlur={(e) => onBlurInput()}
            type={type}
            name={type}
            {...extProps}
          />
        )}
      </div>
      {valid === VALID_STATES.invalid && (
        <p className="input__error-msg">{errorMessage(val)}</p>
      )}
    </Wrapper>
  );
});

Input.displayName = 'Input';

export default Input;
